import { Pressable } from 'react-native';
import styled from 'styled-components/native';
import { REDESIGN_COLORS } from '../../styles/colors';
import { STATUS_LABELS } from '../../config/negotiationConfig';

// --- Styled Components ---

const BannerContainer = styled.View`
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 8px;
  background-color: ${({ $bgColor }) => $bgColor || 'rgba(255, 255, 255, 0.05)'};
  border-width: 1px;
  border-color: ${({ $borderColor }) => $borderColor || REDESIGN_COLORS.borderSubtle};
`;

const BannerRow = styled.View`
  flex-direction: row;
  align-items: center;
`;

const BannerIcon = styled.Text`
  font-size: 16px;
  margin-right: 10px;
`;

const BannerText = styled.Text`
  font-family: 'Satoshi-Medium';
  font-size: 13px;
  line-height: 20px;
  color: ${({ $textColor }) => $textColor || REDESIGN_COLORS.textPrimary};
  flex: 1;
`;

const TERMINAL_STATUSES = ['abandoned', 'closed', 'deal_reached', 'handed_off'];

/**
 * StatusBanner - Conditional banner component for the chat screen.
 *
 * Renders one or more banners depending on the negotiation state:
 * - Car supermarket warning (from evidence_brief?.is_car_supermarket)
 * - Terminal status messages (abandoned, closed, deal_reached, handed_off)
 * - Listing removed/sold (auto_pause_reason='listing_removed')
 * - Listing price changed (latest priceChangeEvent passed in)
 * - Other contextual warnings
 *
 * Returns null when no banners are needed.
 *
 * @param {Object} props
 * @param {Object} props.negotiation - The negotiation object
 * @param {Object} [props.priceChangeEvent] - Latest listing_price_changed event row
 *   (from negotiation_events). Expected metadata: {previous_price, new_price, direction}.
 * @param {Function} [props.onRegenerate] - Optional regenerate handler. When
 *   set and a price-drop banner is shown, the banner exposes a "Regenerate
 *   draft" button so the buyer can refresh the open draft against the new
 *   lower anchor in one tap.
 * @param {boolean} [props.canRegenerate] - Whether regenerate is currently
 *   available (within per-round limit + a draft exists). Hides the button
 *   when false even if onRegenerate is provided.
 * @param {Function} [props.onFindSimilarListing] - Audit C1 (2026-04-30):
 *   Optional handler invoked from the listing-removed banner's primary CTA.
 *   Closes the current negotiation and navigates to the cars feed so the user
 *   can pick a similar listing and start a fresh negotiation. When unset, the
 *   listing-removed banner falls back to the legacy text-only state.
 * @param {Function} [props.onEndNegotiation] - Audit C1: Optional handler
 *   invoked from the listing-removed banner's secondary CTA. Opens the End
 *   negotiation dialog. When unset, the secondary button is hidden.
 */
const StatusBanner = ({
  negotiation,
  priceChangeEvent,
  onRegenerate,
  canRegenerate,
  onFindSimilarListing,
  onEndNegotiation,
}) => {
  if (!negotiation) return null;

  const banners = [];
  const { status, evidence_brief, auto_pause_reason } = negotiation;
  const listingRemoved = auto_pause_reason === 'listing_removed';

  // Car supermarket warning
  const isCarSupermarket = evidence_brief?.is_car_supermarket;
  if (isCarSupermarket) {
    banners.push(
      <BannerContainer
        key="car-supermarket"
        $bgColor="rgba(255, 123, 0, 0.1)"
        $borderColor={REDESIGN_COLORS.scoreMedium}
      >
        <BannerRow>
          <BannerIcon>{'\u26A0'}</BannerIcon>
          <BannerText $textColor={REDESIGN_COLORS.scoreMedium}>
            This is a car supermarket. They typically have less room for
            negotiation on price, but you may be able to negotiate extras
            like warranty, servicing, or accessories.
          </BannerText>
        </BannerRow>
      </BannerContainer>
    );
  }

  // Terminal status banners
  if (TERMINAL_STATUSES.includes(status)) {
    const statusConfig = STATUS_LABELS[status] || {};
    const statusColor = statusConfig.color || REDESIGN_COLORS.textSecondary;

    const messages = {
      abandoned: 'This negotiation was automatically closed due to inactivity.',
      closed: 'This negotiation has been closed.',
      deal_reached: 'Congratulations! This negotiation ended with a deal.',
      handed_off: 'This negotiation has been handed off. Contact the dealer directly.',
    };

    const icons = {
      abandoned: '\u23F0',
      closed: '\u2716',
      deal_reached: '\u2714',
      handed_off: '\u2197',
    };

    const bgColors = {
      abandoned: 'rgba(255, 0, 0, 0.08)',
      closed: 'rgba(255, 255, 255, 0.05)',
      deal_reached: 'rgba(58, 232, 105, 0.1)',
      handed_off: 'rgba(58, 232, 105, 0.1)',
    };

    banners.push(
      <BannerContainer
        key={`status-${status}`}
        $bgColor={bgColors[status]}
        $borderColor={statusColor}
      >
        <BannerRow>
          <BannerIcon>{icons[status]}</BannerIcon>
          <BannerText $textColor={statusColor}>
            {messages[status]}
          </BannerText>
        </BannerRow>
      </BannerContainer>
    );
  }

  // Listing removed / sold — overrides the generic stalled banner because the
  // dealer-non-response copy doesn't fit when the listing itself is gone.
  if (listingRemoved) {
    // Audit C1 (2026-04-30): banner now surfaces a "Find a similar listing"
    // CTA so the user can keep negotiating against a fresh listing instead
    // of being stuck with only an "End negotiation" path.
    banners.push(
      <BannerContainer
        key="listing-removed"
        $bgColor="rgba(255, 0, 0, 0.08)"
        $borderColor={REDESIGN_COLORS.scorePoor}
      >
        <BannerRow>
          <BannerIcon>{'\u26A0'}</BannerIcon>
          <BannerText $textColor={REDESIGN_COLORS.scorePoor}>
            {onFindSimilarListing
              ? 'The listing was removed and may have sold. Want to keep negotiating? Find a similar one and we will start a fresh draft for you.'
              : "The listing was removed and may have sold. We've paused this negotiation. Close it if you've moved on."}
          </BannerText>
        </BannerRow>
        {onFindSimilarListing && (
          <Pressable
            onPress={onFindSimilarListing}
            style={{
              alignSelf: 'flex-start',
              marginTop: 12,
              marginLeft: 26,
              paddingHorizontal: 14,
              paddingVertical: 8,
              borderRadius: 6,
              backgroundColor: REDESIGN_COLORS.ctaBlue,
            }}
          >
            <BannerText style={{ color: '#FFFFFF', fontFamily: 'Satoshi-Bold', fontSize: 13 }}>
              Find a similar listing
            </BannerText>
          </Pressable>
        )}
        {onEndNegotiation && (
          <Pressable
            onPress={onEndNegotiation}
            style={{
              alignSelf: 'flex-start',
              marginTop: 8,
              marginLeft: 26,
              paddingHorizontal: 14,
              paddingVertical: 8,
              borderRadius: 6,
              backgroundColor: 'transparent',
              borderWidth: 1,
              borderColor: REDESIGN_COLORS.borderSubtle,
            }}
          >
            <BannerText style={{ color: REDESIGN_COLORS.textSecondary, fontFamily: 'Satoshi-Medium', fontSize: 13 }}>
              End this negotiation
            </BannerText>
          </Pressable>
        )}
      </BannerContainer>
    );
  } else if (status === 'stalled') {
    banners.push(
      <BannerContainer
        key="stalled"
        $bgColor="rgba(255, 123, 0, 0.08)"
        $borderColor={REDESIGN_COLORS.scoreMedium}
      >
        <BannerRow>
          <BannerIcon>{'\u23F3'}</BannerIcon>
          <BannerText $textColor={REDESIGN_COLORS.scoreMedium}>
            The dealer has not responded for a while. Consider sending a
            follow-up or trying a different channel.
          </BannerText>
        </BannerRow>
      </BannerContainer>
    );
  }

  // Listing price changed during the negotiation. Hide once the negotiation
  // is in a terminal state — the info is no longer actionable.
  if (priceChangeEvent && !TERMINAL_STATUSES.includes(status) && !listingRemoved) {
    const meta = priceChangeEvent.metadata || {};
    // `old_price` is the legacy name used by listingStateRefresher pre-fix;
    // keep it as a fallback so existing rows still render the banner.
    const previous = meta.previous_price ?? meta.original_asking_price ?? meta.old_price;
    const next = meta.new_price;
    if (previous != null && next != null && Number(previous) !== Number(next)) {
      const direction = meta.direction || (Number(next) < Number(previous) ? 'down' : 'up');
      const arrow = direction === 'down' ? '\u2193' : '\u2191';
      const fromStr = `\u00a3${Number(previous).toLocaleString('en-GB')}`;
      const toStr = `\u00a3${Number(next).toLocaleString('en-GB')}`;
      const downCopy = `The listing price dropped from ${fromStr} to ${toStr}. Regenerate your draft so the AI uses ${toStr} as the new anchor: your next offer will be calibrated against the lower asking price.`;
      const upCopy = `The listing price went up from ${fromStr} to ${toStr}. The AI will continue from your existing offer.`;
      banners.push(
        <BannerContainer
          key={`price-change-${priceChangeEvent.id}`}
          $bgColor="rgba(58, 232, 105, 0.08)"
          $borderColor={REDESIGN_COLORS.scoreGood}
        >
          <BannerRow>
            <BannerIcon>{arrow}</BannerIcon>
            <BannerText $textColor={REDESIGN_COLORS.scoreGood}>
              {direction === 'down' ? downCopy : upCopy}
            </BannerText>
          </BannerRow>
          {direction === 'down' && onRegenerate && canRegenerate && (
            <Pressable
              onPress={onRegenerate}
              style={{
                alignSelf: 'flex-start',
                marginTop: 10,
                marginLeft: 26,
                paddingHorizontal: 14,
                paddingVertical: 8,
                borderRadius: 6,
                backgroundColor: REDESIGN_COLORS.scoreGood,
              }}
            >
              <BannerText style={{ color: '#0E1726', fontFamily: 'Satoshi-Bold', fontSize: 13 }}>
                Regenerate draft
              </BannerText>
            </Pressable>
          )}
        </BannerContainer>
      );
    }
  }

  if (banners.length === 0) return null;

  return <>{banners}</>;
};

export default StatusBanner;
